// src/components/share/ExportDialog.tsx
import React, { useState, useRef } from 'react';
import { ExportEngineService, ExportProgress } from '../../services/exportService';
import { StorageService, storageService as defaultStorageService } from '../../services/storage';
import { X, Download, CheckCircle, AlertCircle } from 'lucide-react';

export interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tripId?: number | string;
  storageService?: StorageService;
}

export const ExportDialog: React.FC<ExportDialogProps> = ({
  isOpen,
  onClose,
  tripId,
  storageService = defaultStorageService,
}) => {
  const [includeHd, setIncludeHd] = useState<boolean>(true);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [exportedDetails, setExportedDetails] = useState<{
    fileName: string;
    totalBytes: number;
    fileCount: number;
    durationMs: number;
  } | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  if (!isOpen) return null;

  const handleStartExport = async () => {
    setIsExporting(true);
    setError(null);
    setSuccess(false);
    setProgress(null);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const service = new ExportEngineService(storageService);
      const result = await service.exportTripPackage(tripId, {
        includeHdPreviews: includeHd,
        signal: controller.signal,
        onProgress: (percent, currentFileName, details) => {
          if (details) {
            setProgress(details);
          } else {
            setProgress({
              percent,
              currentFileName,
              filesProcessed: 0,
              totalFiles: 0,
              bytesProcessed: 0,
              totalBytes: 0,
            });
          }
        },
      });

      setExportedDetails(result);
      setSuccess(true);
      setIsExporting(false);
    } catch (err: any) {
      if (err.name === 'AbortError') {
        setError('Esportazione annullata.');
      } else {
        setError(err.message || 'Errore imprevisto durante l\'esportazione.');
      }
      setIsExporting(false);
    }
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl text-slate-100 flex flex-col gap-5 relative">
        <button
          type="button"
          onClick={() => {
            if (isExporting) handleCancel();
            onClose();
          }}
          className="absolute top-4 right-4 w-7 h-7 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-500/20 border border-sky-500/30 flex items-center justify-center text-sky-400">
            <Download className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Esporta Pacchetto ZIP</h3>
            <p className="text-xs text-slate-400">Standalone Viewer Offline & R2 Ready</p>
          </div>
        </div>

        {!isExporting && !success && (
          <div className="space-y-4">
            <p className="text-xs text-slate-300 leading-relaxed">
              Crea un archivio compresso contenente tutte le foto WebP ottimizzate, metadati JSON, il percorso tracciato e un visualizzatore HTML autonomo per aprire il viaggio con un doppio click su qualsiasi computer.
            </p>

            <label className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-800/50 border border-slate-700/60 cursor-pointer">
              <input
                type="checkbox"
                checked={includeHd}
                onChange={(e) => setIncludeHd(e.target.checked)}
                className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500 bg-slate-900 border-slate-700"
              />
              <div className="text-xs">
                <span className="font-semibold text-slate-200">Includi anteprime HD (1600px)</span>
                <p className="text-[11px] text-slate-400">Consente la visualizzazione ad alta risoluzione a schermo intero.</p>
              </div>
            </label>

            {error && (
              <div className="p-3 rounded-xl bg-red-950/60 border border-red-800/60 text-red-200 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="button"
              onClick={handleStartExport}
              className="w-full py-2.5 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-xs font-semibold shadow-md shadow-blue-500/20 transition flex items-center justify-center gap-2 cursor-pointer"
            >
              <Download className="w-4 h-4" />
              <span>Avvia Esportazione Streaming</span>
            </button>
          </div>
        )}

        {isExporting && progress && (
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-300 font-medium">Esportazione in corso...</span>
              <span className="font-mono text-sky-400 font-bold">{progress.percent}%</span>
            </div>

            <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden border border-slate-700">
              <div
                className="h-full bg-gradient-to-r from-sky-500 to-blue-600 transition-all duration-150"
                style={{ width: `${progress.percent}%` }}
              />
            </div>

            <div className="space-y-1">
              <p className="text-[11px] text-slate-400 truncate font-mono">
                {progress.currentFileName}
              </p>
              {progress.totalFiles > 0 && (
                <p className="text-[10px] text-slate-500">
                  File {progress.filesProcessed} di {progress.totalFiles} • Memoria RAM: &lt; 2 MB
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={handleCancel}
              className="w-full mt-2 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl text-xs font-medium border border-slate-700 transition cursor-pointer"
            >
              Annulla
            </button>
          </div>
        )}

        {success && exportedDetails && (
          <div className="space-y-4 py-2 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center mx-auto">
              <CheckCircle className="w-6 h-6" />
            </div>

            <div className="space-y-1">
              <h4 className="text-sm font-bold text-white">Pacchetto Generato con Successo!</h4>
              <p className="text-xs text-slate-400 font-mono break-all">{exportedDetails.fileName}</p>
            </div>

            <div className="p-3 bg-slate-800/60 rounded-xl border border-slate-700/60 text-xs text-slate-300 grid grid-cols-2 gap-2 text-left">
              <div>
                <span className="text-slate-400 text-[10px] block">Totale File:</span>
                <span className="font-bold font-mono">{exportedDetails.fileCount}</span>
              </div>
              <div>
                <span className="text-slate-400 text-[10px] block">Dimensione:</span>
                <span className="font-bold font-mono">
                  {(exportedDetails.totalBytes / (1024 * 1024)).toFixed(1)} MB
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-semibold border border-slate-700 transition cursor-pointer"
            >
              Chiudi
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
